import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
  Pagination,
  Typography,
  CircularProgress,
  Paper,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useUser from '../../hooks/useUser';
import { useAuth } from '../../context/authContext';
import useBranch from '../../hooks/useBranch';
import useHttp from '../../hooks/useHttp';
import { toast } from 'react-toastify';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const TimebutlerUserConversion = () => {
  const theme = useTheme();
  const {
    getUnmatchedTimebutlerUsers,
    convertTimebutlerUser,
    setTimebutlerUserIgnored,
    getTimebutlerDepartments,
    batchActivateBranch,
    getMyBranches,
  } = useUser();
  const { getBranches } = useBranch();
  const { isLoading } = useHttp();
  const { checkRight } = useAuth();
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [branchOptions, setBranchOptions] = useState([]);
  const [showIgnored, setShowIgnored] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [data, setData] = useState({ users: [], pagination: { page: 1, limit: 20, total: 0 } });
  const [activatingIds, setActivatingIds] = useState(new Set());
  const canActivateUsers = checkRight('user:create:own') || checkRight('user:special:manageCcEmployees');

  const fetchData = async (pageToLoad = page) => {
    try {
      const resp = await getUnmatchedTimebutlerUsers({
        page: pageToLoad,
        limit,
        search,
        branchId,
        department,
        showIgnored,
      });
      setData(resp || { users: [], pagination: { page: 1, limit, total: 0 } });
      setPage(resp?.pagination?.page || pageToLoad);
    } catch (e) {
      toast.error('Fehler beim Laden der Timebutler-Nutzer');
    }
  };

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, showIgnored]);

  useEffect(() => {
    const init = async () => {
      try {
        const [myBranches, departments] = await Promise.all([
          getMyBranches(),
          getTimebutlerDepartments(),
        ]);
        // Scope the location filter to the user's assigned branches, mirroring how the
        // backend scopes the unmatched-timebutler list. This works for users with multiple
        // assigned branches and does not depend on the branch:read:own right (the list view
        // only requires authentication). Full-access users without assigned branches fall
        // back to the complete branch list.
        let branches = myBranches?.branches || [];
        if (!myBranches?.isScopedUser || branches.length === 0) {
          branches = await getBranches();
        }
        setBranchOptions((branches || []).map(b => ({ value: b._id, label: b.locationName })));
        setDepartmentOptions((departments || []).map(d => ({ value: d, label: d })));
      } catch (e) {
        // ignore init errors
      }
    };
    init();
  }, []);

  const onSearch = () => fetchData(1);

  const handleConvert = async user => {
    try {
      setActivatingIds(prev => {
        const next = new Set(prev);
        next.add(user.userId);
        return next;
      });
      await convertTimebutlerUser(user.userId);
      toast.success('Nutzer erfolgreich aktiviert');
      fetchData(page);
    } catch (e) {
      toast.error('Fehler beim Aktivieren des Nutzers');
    } finally {
      setActivatingIds(prev => {
        const next = new Set(prev);
        next.delete(user.userId);
        return next;
      });
    }
  };

  const handleIgnore = async (user, nextIgnore) => {
    try {
      await setTimebutlerUserIgnored(user.userId, nextIgnore);
      toast.success(nextIgnore ? 'Nutzer ignoriert' : 'Ignorierung aufgehoben');
      fetchData(page);
    } catch (e) {
      toast.error('Fehler beim Aktualisieren des Ignorier-Status');
    }
  };

  const totalPages = useMemo(
    () =>
      Math.max(1, Math.ceil((data?.pagination?.total || 0) / (data?.pagination?.limit || limit))),
    [data, limit]
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, ...viewRootTextPrimarySx }}>
      <Stack spacing={2.5}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant='h5'>Mitarbeiter Accounts aktivieren</Typography>
              <Typography variant='body2' color='text.secondary'>
                Timebutler-Nutzer suchen, prüfen und als Mitarbeiterkonto übernehmen.
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems='center'>
              <Grid item xs={12} sm={6} lg={3}>
                <TextField
                  fullWidth
                  label='Suche'
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && onSearch()}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <FormControl fullWidth>
                  <InputLabel id='dept-label'>Department</InputLabel>
                  <Select
                    labelId='dept-label'
                    label='Department'
                    value={department}
                    onChange={e => setDepartment(e.target.value)}
                  >
                    <MenuItem value=''>Alle</MenuItem>
                    {departmentOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <FormControl fullWidth>
                  <InputLabel id='branch-label'>Branch</InputLabel>
                  <Select
                    labelId='branch-label'
                    label='Branch'
                    value={branchId}
                    onChange={e => setBranchId(e.target.value)}
                  >
                    <MenuItem value=''>Alle</MenuItem>
                    {branchOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} lg={1}>
                <FormControl fullWidth>
                  <InputLabel id='limit-label'>Limit</InputLabel>
                  <Select
                    labelId='limit-label'
                    value={limit}
                    label='Limit'
                    onChange={e => setLimit(e.target.value)}
                  >
                    {[10, 20, 50, 100].map(n => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} lg={2}>
                <FormControlLabel
                  sx={{ m: 0, minHeight: 56, alignItems: 'center' }}
                  control={
                    <Checkbox
                      checked={showIgnored}
                      onChange={e => setShowIgnored(e.target.checked)}
                    />
                  }
                  label='Ignorierte anzeigen'
                />
              </Grid>
            </Grid>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant='contained' onClick={onSearch} disabled={isLoading}>
                Suchen
              </Button>
              {checkRight('user:special:batchActivateBranch') && (
                <Button
                  variant='outlined'
                  color='success'
                  disabled={!branchId || isLoading}
                  onClick={async () => {
                    try {
                      const data = await batchActivateBranch({ branchId, includeIgnored: false });
                      toast.success(`Aktiviert: ${data.activated} / ${data.totalCandidates}`);
                      fetchData(page);
                    } catch (e) {
                      toast.error('Batch-Aktivierung fehlgeschlagen');
                    }
                  }}
                >
                  Branch aktivieren
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        <Stack spacing={1.5}>
          {data.users && data.users.length > 0 ? (
            <>
              {data.users.map(u => (
                <Paper
                  key={u.userId}
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    bgcolor: u.ignore ? 'action.hover' : 'background.paper',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent='space-between'
                  >
                    <Stack spacing={1} sx={{ minWidth: 0 }}>
                      <Box>
                        <Typography variant='subtitle1' fontWeight={700}>
                          {u.firstName} {u.lastName}
                        </Typography>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ overflowWrap: 'anywhere' }}
                        >
                          {u.email || '-'}
                        </Typography>
                      </Box>

                      <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                        <Chip size='small' label={u.department || 'Kein Department'} />
                        <Chip size='small' label={u.branchOffice || 'Keine Branch'} />
                        {u.ignore && <Chip size='small' color='warning' label='Ignoriert' />}
                      </Stack>

                      <Divider flexItem sx={{ display: { xs: 'block', md: 'none' } }} />

                      {u.departmentMapping ? (
                        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                          <Typography variant='caption' color='text.secondary'>
                            Rolle: {u.departmentMapping.roleId?.name || '—'}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            Branch zuweisen: {u.departmentMapping.assignBranch ? 'Ja' : 'Nein'}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant='caption' color='warning.main'>
                          Keine Rollen-Zuordnung vorhanden
                        </Typography>
                      )}
                    </Stack>

                    <Stack
                      direction={{ xs: 'column', sm: 'row', md: 'column', lg: 'row' }}
                      spacing={1}
                      alignItems='stretch'
                      sx={{ flexShrink: 0, minWidth: { md: 150 } }}
                    >
                      {canActivateUsers && (
                        <Button
                          size='small'
                          variant='outlined'
                          onClick={() => handleConvert(u)}
                          disabled={!u.departmentMapping || activatingIds.has(u.userId)}
                          endIcon={
                            activatingIds.has(u.userId) ? (
                              <CircularProgress color='inherit' size={16} />
                            ) : null
                          }
                        >
                          Aktivieren
                        </Button>
                      )}
                      {checkRight('user:update:own') && (
                        <Button
                          size='small'
                          variant='text'
                          color={u.ignore ? 'warning' : 'inherit'}
                          onClick={() => handleIgnore(u, !u.ignore)}
                        >
                          {u.ignore ? 'Ignorierung aufheben' : 'Ignorieren'}
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              ))}

              <Box display='flex' justifyContent='center'>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, p) => fetchData(p)}
                  color='primary'
                  size='small'
                  sx={{
                    '& .MuiPagination-ul': {
                      justifyContent: 'center',
                    },
                  }}
                />
              </Box>
            </>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                textAlign: 'center',
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant='body1'>Keine Einträge gefunden.</Typography>
            </Paper>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default TimebutlerUserConversion;
